// -------------------------------------------------------------------------
// GENERATED FILE — do not hand-edit.
//
// Generated by scripts/generate-tokens.js from src/tokens.json.
// Re-generate with `npm run tokens:build`. Edit src/tokens.json (or the
// generator) instead — hand edits here are overwritten and fail
// tests/unit/tokens-generated.spec.ts.
// -------------------------------------------------------------------------

// -------------------------------------------------------------------------
// tokens/colors.css
// -------------------------------------------------------------------------
export const colors = {
  // ---- Base palette (raw brand values) ----
  blytzNavyDark: '#222e3e',
  blytzNavy: '#304258',
  blytzNavyLight: '#3f5876',
  blytzMagenta: '#dd1682',
  blytzMagentaHover: '#b31269',
  blytzMagentaActive: '#660a3c',
  blytzMagentaTint: '#f9d5e9',
  blytzSteel: '#8193a9',
  blytzGray: '#636c73',
  blytzBorder: '#dee2e6',
  blytzSurface: '#f0f3f7',
  blytzInverseMuted: '#c9d2dc',
  blytzWhite: '#ffffff',
  blytzBlack: '#000000',
  blytzRed: '#c1272d',
  blytzRedHover: '#9b1f24',
  blytzRedActive: '#5d1316',
  blytzRedTint: '#f4d8d9',
  blytzAmber: '#fdb913',
  blytzAmberStrong: '#6f4f01',
  blytzAmberTint: '#fff2d5',
  blytzGreen: '#00be6f',
  blytzGreenStrong: '#007041',
  blytzGreenTint: '#d1f3e5',
  blytzCyan: '#17a2b8',
  blytzCyanStrong: '#0c5864',
  blytzCyanTint: '#d5eef2',

  // Logo gradient — fixed, never re-themed
  blytzLogoStop1: '#ac218c',
  blytzLogoStop2: '#f68b1f',
  blytzLogoStop3: '#5bc4bf',

  // ---- Semantic text ----
  colorTextHeading: '#222e3e',
  colorTextBody: '#304258',
  colorTextSecondary: '#636c73',
  colorTextInverse: '#ffffff',
  colorTextInverseMuted: '#c9d2dc',

  // ---- Semantic surfaces ----
  colorSurfacePage: '#f0f3f7',
  colorSurfaceRaised: '#ffffff',
  colorSurfaceInverse: '#222e3e',

  // ---- Borders ----
  // decorative fails the 3:1 non-text floor — never bound an interactive
  // control with it.
  colorBorderDecorative: '#dee2e6',
  colorBorderDecorativeStrong: '#8193a9',
  colorBorderControl: '#636c73',

  // ---- Actions ----
  colorActionPrimary: '#dd1682',
  colorActionPrimaryHover: '#b31269',
  colorActionPrimaryActive: '#660a3c',
  colorActionPrimaryDisabled: '#f9d5e9',
  colorActionSecondary: '#304258',
  colorActionSecondaryDisabled: '#dadde1', // 18% navy over white
  colorActionSecondaryHover: '#222e3e',
  colorActionSecondaryActive: '#10161d',
  colorActionSecondaryAlt: '#3f5876',
  colorActionSecondaryAltDisabled: '#dce1e6', // 18% light navy over white
  colorActionSecondaryAltHover: '#304258',
  colorActionSecondaryAltActive: '#273749',
  colorActionDisabled: '#8193a9',

  // ---- Status ----
  colorStatusDanger: '#c1272d',
  colorStatusDangerHover: '#9b1f24',
  colorStatusDangerActive: '#5d1316',
  colorStatusDangerSubtle: '#f4d8d9',
  colorStatusWarning: '#fdb913',
  colorStatusWarningStrong: '#6f4f01',
  colorStatusWarningSubtle: '#fff2d5',
  colorStatusSuccess: '#00be6f',
  colorStatusSuccessStrong: '#007041',
  colorStatusSuccessSubtle: '#d1f3e5',
  colorStatusInfo: '#17a2b8',
  colorStatusInfoStrong: '#0c5864',
  colorStatusInfoSubtle: '#d5eef2',

  // ---- Overlays, scrim, focus ----
  colorScrim: 'rgba(34, 46, 62, 0.55)',
  colorFocusRing: '#8193a9',
  colorFocusRingInverse: 'rgba(255, 255, 255, 0.65)',
  colorOverlaySubtle: 'rgba(255, 255, 255, 0.06)',
  colorOverlayMedium: 'rgba(255, 255, 255, 0.12)',
  colorSpinnerTrack: 'rgba(255, 255, 255, 0.4)',
  opacityDisabled: '0.53',
  opacityMuted: '0.8'
} as const

export type ColorTokenName = keyof typeof colors

// -------------------------------------------------------------------------
// tokens/typography.css
// -------------------------------------------------------------------------
export const typography = {
  fontBody: 'Roboto, sans-serif',
  fontDisplay: '"Roboto Condensed", sans-serif',
  weightRegular: '400',
  weightMedium: '500',
  weightDisplay: '700',
  trackingLabel: '0.08em', // the only place tracking is set

  // Type scale — px throughout. Anything that can render a dollar amount
  // also takes font-variant-numeric: tabular-nums.
  typeDisplaySize: '41px', // Condensed 300
  typeDisplayLh: '1.15',
  typeTitleSize: '32px', // Condensed 700
  typeTitleLh: '1.2',
  typeHeadingSize: '24px', // Condensed 700
  typeHeadingLh: '1.25',
  typeHeadingSmSize: '20px', // Condensed 700
  typeHeadingSmLh: '1.3',
  typeSubheadSize: '18px', // Roboto 500
  typeSubheadLh: '1.4',
  typeBodySize: '16px', // Roboto 400
  typeBodyLh: '1.6',
  typeBodySmSize: '14px', // Roboto 400
  typeBodySmLh: '1.5',
  typeLabelSize: '14px', // Condensed 700 uppercase
  typeLabelLh: '1.3',
  typeCaptionSize: '12px', // Roboto 400
  typeCaptionLh: '1.4',
  typeFinePrintSize: '11px', // Roboto 400
  typeFinePrintLh: '1.5'
} as const

export type TypographyTokenName = keyof typeof typography

// -------------------------------------------------------------------------
// tokens/spacing.css
// -------------------------------------------------------------------------
export const spacing = {
  // One 4px ramp. Every legacy padding maps onto the nearest step.
  space1: '4px', // icon-to-label gaps
  space2: '8px', // tight stacks, chip padding
  space3: '12px', // input/button vertical padding, list row gaps
  space4: '16px', // card padding, button horizontal padding
  space5: '20px', // drawer/modal interior padding
  space6: '24px', // section-internal stack gaps
  space7: '32px', // between stacked cards/groups
  space8: '40px', // desktop page gutter
  gutterMobile: '24px',
  gutterDesktop: '40px'
} as const

export type SpacingTokenName = keyof typeof spacing

// -------------------------------------------------------------------------
// tokens/layout.css
// -------------------------------------------------------------------------
export const layout = {
  // Every screen is single-column and mobile-first: what varies is
  // container width.
  containerCard: '400px', // primary account card — the core content column
  containerNavDrawer: '280px',
  containerDrawer: '420px', // bottom drawers / sheets
  containerPanel: '340px', // side settings panel (capped at 88vw)
  breakpointMobile: '600px', // below: full-bleed, 24px gutters
  breakpointDesktop: '960px', // at/above: same as tablet, no multi-column layout

  // Control heights — matches what ships (36/44/48), plus the lg hero step
  controlHeightSm: '36px',
  controlHeightMd: '44px',
  controlHeightLg: '52px',
  iconInline: '16px', // glyphs in a line of text
  iconControl: '20px', // glyphs in a control's hit area
  iconStroke: '1.5px',
  zPage: '0',
  zNav: '10',
  zDropdown: '20',
  zDrawerScrim: '40',
  zDrawer: '41',
  zModalScrim: '50',
  zModal: '51',
  zToast: '60',
  timingToastDismiss: '5000ms',
  timingSessionWarning: '60000ms',
  countToastMax: '3'
} as const

export type LayoutTokenName = keyof typeof layout

// -------------------------------------------------------------------------
// tokens/shape.css
// -------------------------------------------------------------------------
export const shape = {
  // Radius — fields and controls genuinely ship at different radii; kept split.
  radiusField: '4px', // inputs, selects, chips, skeleton bars
  radiusControl: '8px', // buttons, dropdown menus, alerts, toasts, nav rows
  radiusContainer: '12px', // cards, modals, drawer top, nav panel bottom
  radiusPill: '999px', // badges, avatars, toggle tracks
  borderWidthDefault: '1px', // resting borders
  borderWidthEmphasis: '1.5px', // validated/errored inputs, selected & pressed cards
  borderWidthAccent: '4px', // status-alert left rules
  borderWidthFocus: '2px', // focus-ring spread — never a border
  shadowSm: '0 1px 2px rgba(0, 0, 0, 0.06)', // list rows, chips
  shadowMd: '0 4px 12px rgba(0, 0, 0, 0.08)', // cards, dropdown menus
  shadowLg: '0 20px 40px rgba(0, 0, 0, 0.16)', // modals, drawers, session dialog
  shadowFloat: '0 4px 12px rgba(0, 0, 0, 0.16)', // toasts: md geometry, lg opacity

  // Focus is a composite, not a color: ring + width + 0 offset + solid.
  focusRing: '0 0 0 2px #8193a9',
  focusRingInverse: '0 0 0 2px rgba(255, 255, 255, 0.65)'
} as const

export type ShapeTokenName = keyof typeof shape

// -------------------------------------------------------------------------
// tokens/motion.css
// -------------------------------------------------------------------------
export const motion = {
  durationInstant: '100ms', // hover, press, focus ring
  durationFast: '200ms', // toasts, inline errors
  durationBase: '250ms', // drawers, sheets, nav panel
  durationSpin: '700ms',
  durationShimmer: '1400ms',
  easeEnter: 'cubic-bezier(0, 0, 0.2, 1)', // everything arriving
  easeExit: 'cubic-bezier(0.4, 0, 1, 1)' // everything leaving
} as const

export type MotionTokenName = keyof typeof motion
